import { API, graphqlOperation } from 'aws-amplify'
import { useEffect, useState } from 'react'

const op = `query ListOccupants($d1: String, $sk: String, $sk2: String, $nextToken: String, $limit: Int) {
    listOccupants(d1: $d1, sk: $sk, sk2: $sk2, nextToken: $nextToken, limit: $limit) {
          items {
            pk
            sk
            d1
            d2
            email
            firstName
            lastLogin
            registrationDate
            lastName
            primaryContact
            phone {
                number
                extension
            }
            roles
            workPhone {
                number
                extension
            }
            extraFields {
                key
                value
            }
          }
          nextToken
        }
      }
`

const cache = {}
const CACHE_VERSION = 4 // Increment to invalidate old cache - increased to force fresh fetch

// Global cache invalidation support - can be called from anywhere to force refresh
let cacheInvalidationTimestamp = 0

export function invalidateOccupantsCache() {
    cacheInvalidationTimestamp = Date.now()
    // Also clear the cache object
    Object.keys(cache).forEach(key => delete cache[key])
}

// Module-level listener ensures the cache is cleared even when no hook instance is
// mounted (e.g. user navigated away from the Users page before creating an association).
if (typeof window !== 'undefined') {
    window.addEventListener('occupant-data-updated', () => {
        invalidateOccupantsCache()
    })
}

function useListOccupants(sk, d1, nextToken, limit, sk2, forceRefresh = false) {
    const [error, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([])
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    const handleUsers = (data) => {
        setData(data.items)
        setLoading(false)
    }

    // Listen for occupant data updates (archive/restore/create/delete)
    useEffect(() => {
        const handleOccupantUpdate = (event) => {
            console.log('[useListOccupants] Received occupant-data-updated event:', event.detail);
            // Invalidate cache and trigger refetch
            invalidateOccupantsCache();
            setRefreshTrigger(prev => prev + 1);
        };

        window.addEventListener('occupant-data-updated', handleOccupantUpdate);

        return () => {
            window.removeEventListener('occupant-data-updated', handleOccupantUpdate);
        };
    }, []);

    useEffect(() => {
        async function fetch() {
            const cacheKey = sk2 ? `${sk2}-${d1}` : sk
            let cached = cache[cacheKey]

            // Check cache version - invalidate if old version
            if (cached && cached.version !== CACHE_VERSION) {
                cached = null
                delete cache[cacheKey]
            }

            // Skip cache if force refresh is enabled
            if (forceRefresh) {
                cached = null;
                delete cache[cacheKey];
            }

            // Return cached data immediately if available
            if (cached) {
                handleUsers(cached)
                return
            }

            // Set loading to true when starting a fresh fetch
            setLoading(true)

            try {
                let allItems = []
                let currentNextToken = null // Start from first page
                let pageCount = 0
                const MAX_PAGES = 50 // Safety limit
                const seenUserTenantPairs = new Set() // Track unique user+tenant combinations
                let totalDuplicates = 0

                // Fetch all pages
                do {
                    pageCount++

                    // Safety check
                    if (pageCount > MAX_PAGES) {
                        console.warn(`[useListOccupants] Reached max ${MAX_PAGES} pages, stopping. Total: ${allItems.length}`)
                        break
                    }

                    const params = {
                        d1: d1,
                        sk: sk,
                        sk2: sk2,
                        nextToken: currentNextToken,
                        limit: limit,
                    }

                    const response = await API.graphql(graphqlOperation(op, params))
                    const result = response.data.listOccupants

                    // Add items from this page, deduplicating by user+tenant combination
                    if (result.items && result.items.length > 0) {
                        let addedThisPage = 0
                        let duplicatesThisPage = 0

                        for (const item of result.items) {
                            // Extract tenant ID from sk field (format: OCCUPANCY#tenantId)
                            const tenantId = item.sk?.split('#')[1]

                            // Create unique key from user pk + tenant ID
                            // This allows same user to appear in multiple tenants, but only once per tenant
                            const uniqueKey = `${item.pk}|${tenantId}`

                            if (item.pk && !seenUserTenantPairs.has(uniqueKey)) {
                                allItems.push(item)
                                seenUserTenantPairs.add(uniqueKey)
                                addedThisPage++
                            } else {
                                duplicatesThisPage++
                                totalDuplicates++
                            }
                        }
                    } else {
                        break // No more items
                    }

                    currentNextToken = result.nextToken

                } while (currentNextToken)

                // Cache results with version
                cached = { items: allItems, nextToken: null, version: CACHE_VERSION }
                cache[cacheKey] = cached
                handleUsers(cached)
            } catch (response) {
                console.error(response.errors)
                setErrors(response.errors)
                setLoading(false)
            }
        }

        // If using sk2, require d1 instead of sk
        if (sk2 && !d1) {
            setLoading(false)
            return
        }

        // If using sk, require it to be present
        if (!sk2 && !sk) {
            setLoading(false)
            return
        }

        fetch()
    }, [sk, d1, sk2, limit, forceRefresh, refreshTrigger])

    return { error, loading, data }
}

export default useListOccupants

